import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { integrationsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'

// ============================================
// INTEGRATION TYPES
// ============================================

export interface Integration {
  id: string
  name: string
  provider: 'splunk'
  is_active: boolean
  field_mapping?: Record<string, string>
  filters: { min_severity?: number }
  last_dispatched_at?: string
  created_at: string
}

export interface IntegrationListResponse {
  count: number
  next: string | null
  previous: string | null
  results: Integration[]
}

export interface CreateIntegrationRequest {
  name: string
  provider: string
  connection_config: Record<string, any>
  field_mapping?: Record<string, string>
  filters?: { min_severity?: number }
}

export interface UpdateIntegrationRequest {
  name?: string
  is_active?: boolean
  connection_config?: Record<string, any>
  field_mapping?: Record<string, string>
  filters?: { min_severity?: number }
}

// ============================================
// INTEGRATION QUERIES
// ============================================

/**
 * Hook for fetching integrations list with pagination
 */
export function useIntegrations() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.integrations.list(orgId, params),
    queryFn: () => integrationsApi.getList(params),
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching a specific integration detail
 */
export function useIntegrationDetail(integrationId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.integrations.detail(orgId, integrationId || ''),
    queryFn: () => integrationsApi.getById(integrationId!),
    enabled: !!integrationId && !!orgId,
  })
}

/**
 * Hook for fetching field mapping metadata
 */
export function useFieldMappingInfo() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.integrations.fieldMapping(orgId),
    queryFn: () => integrationsApi.getFieldMappingInfo(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching available providers and their config schemas
 */
export function useIntegrationProviders() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.integrations.providers(orgId),
    queryFn: () => integrationsApi.getProviders(),
    staleTime: 10 * 60 * 1000, // 10 minutes — rarely changes
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching dispatch events (paginated)
 */
export function useIntegrationEvents(integrationId: string | null, page: number) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.integrations.events(orgId, integrationId || '', page),
    queryFn: () => integrationsApi.getEvents(integrationId!, page),
    enabled: !!integrationId && !!orgId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

/**
 * Hook for fetching integration statistics
 */
export function useIntegrationStatistics(integrationId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.integrations.statistics(orgId, integrationId || ''),
    queryFn: () => integrationsApi.getStatistics(integrationId!),
    enabled: !!integrationId && !!orgId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// ============================================
// INTEGRATION MUTATIONS
// ============================================

/**
 * Hook for creating a new integration
 */
export function useCreateIntegration() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: CreateIntegrationRequest) => integrationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all(orgId) })
      toast.success('Integration created successfully')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for updating an integration
 */
export function useUpdateIntegration() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      integrationId,
      data,
    }: {
      integrationId: string
      data: UpdateIntegrationRequest
    }) => integrationsApi.update(integrationId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all(orgId) })
      queryClient.invalidateQueries({
        queryKey: queryKeys.integrations.detail(orgId, variables.integrationId),
      })
      toast.success('Integration updated successfully')
    },
    onError: (error: any) => {
    },
  })
}

/**
 * Hook for deleting an integration
 */
export function useDeleteIntegration() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (integrationId: string) => integrationsApi.delete(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.integrations.all(orgId) })
      toast.success('Integration deleted successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for testing an integration connection
 */
export function useTestConnection() {
  return useMutation({
    mutationFn: (integrationId: string) => integrationsApi.testConnection(integrationId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Connected successfully${data.latency_ms != null ? ` (${data.latency_ms}ms)` : ''}`)
      } else {

      }
    },
    onError: (error: any) => {

    },
  })
}
